import classnames from "classnames";
import React from "react";

export type BadgeColor =
  | "success"
  | "warning"
  | "error"
  | "info"
  | "primary"
  | "disabled";

interface BadgeProps {
  label: string | JSX.Element;
  size?: "xs" | "sm" | "lg";
  type: BadgeColor;
  className?: string;
  dataCy?: string;
}

export const Badge = ({
  label,
  type,
  size = "xs",
  className = "",
  dataCy,
}: BadgeProps) => {
  const sizeClasses =
    size === "xs"
      ? "text-[10px] px-2 py-0.5"
      : size === "sm"
        ? "text-xs px-2.5 py-0.5"
        : "text-sm px-3 py-1";
  const dotClasses =
    size === "xs"
      ? "h-1.5 w-1.5 mr-1.5"
      : size === "sm"
        ? "h-2 w-2 mr-2"
        : "h-2.5 w-2.5 mr-2.5";

  const colorClasses = {
    primary:
      "border-primary-300/30 bg-primary-500/10 text-primary-800 dark:border-primary-500/25 dark:bg-primary-500/15 dark:text-primary-200",
    success:
      "border-success-300/30 bg-success-500/10 text-success-800 dark:border-success-500/25 dark:bg-success-500/15 dark:text-success-200",
    error:
      "border-error-300/30 bg-error-500/10 text-error-800 dark:border-error-500/25 dark:bg-error-500/15 dark:text-error-200",
    warning:
      "border-warning-300/30 bg-warning-500/10 text-warning-800 dark:border-warning-500/25 dark:bg-warning-500/15 dark:text-warning-200",
    info:
      "border-info-300/30 bg-info-500/10 text-info-800 dark:border-info-500/25 dark:bg-info-500/15 dark:text-info-200",
    disabled:
      "border-content-300/30 bg-content-300/15 text-content-700 dark:border-content-700/30 dark:bg-content-700/15 dark:text-content-300",
  } as const;

  const dotColorClasses = {
    primary: "bg-primary-500",
    success: "bg-success-500",
    error: "bg-error-500",
    warning: "bg-warning-500",
    info: "bg-info-500",
    disabled: "bg-content-400",
  } as const;

  return (
    <div
      data-cy={dataCy}
      className={classnames(
        "inline-flex w-fit items-center rounded-full border font-sans font-semibold backdrop-blur-sm",
        sizeClasses,
        colorClasses[type],
        className
      )}
    >
      <span className={classnames(
        "rounded-full",
        dotClasses,
        dotColorClasses[type]
      )} />
      {label}
    </div>
  );
};
